/**
 * 🛡️ شارة الموثوقية الموحّدة
 *
 * تستخدم لعرض مستوى التحقق من المعلن في جميع أنحاء الموقع:
 * - بطاقات العقارات (PropertyCard)
 * - تفاصيل العقار (PropertyDetailPage)
 * - بطاقات الوسطاء (BrokerCard)
 * - أي مكان يعرض معلومات معلن
 *
 * المستويات:
 * - 💎 موثّق ذهبي: Nafath + FAL ساري + سجل > 6 شهور
 * - ✓ موثّق: Nafath + FAL ساري
 * - 🔒 محقّق هوية: Nafath فقط
 * - ⚠️ غير موثّق: لا شيء
 */

export interface AdvertiserVerification {
  nafathVerified?: boolean | null;
  nafathVerifiedAt?: string | Date | null;
  falLicenseStatus?: string | null;
  falLicenseExpiresAt?: string | Date | null;
  createdAt?: string | Date | null;
}

export type VerificationTier = 'DIAMOND' | 'VERIFIED' | 'IDENTITY' | 'NONE';

/** يحسب مستوى الموثوقية بناءً على بيانات المستخدم */
export function computeVerificationTier(u: AdvertiserVerification | null | undefined): VerificationTier {
  if (!u) return 'NONE';

  const nafathOk = !!u.nafathVerified;

  const falOk =
    u.falLicenseStatus === 'APPROVED' &&
    (!u.falLicenseExpiresAt || new Date(u.falLicenseExpiresAt) > new Date());

  // 💎 ذهبي = نفاذ + فال ساري + سجل أكثر من 6 شهور
  if (nafathOk && falOk && u.createdAt) {
    const sixMonthsMs = 1000 * 60 * 60 * 24 * 180;
    const accountAge = Date.now() - new Date(u.createdAt).getTime();
    if (accountAge > sixMonthsMs) return 'DIAMOND';
  }

  // ✓ موثّق = نفاذ + فال ساري
  if (nafathOk && falOk) return 'VERIFIED';

  // 🔒 محقّق هوية = نفاذ فقط
  if (nafathOk) return 'IDENTITY';

  return 'NONE';
}

interface BadgeProps {
  user: AdvertiserVerification | null | undefined;
  /** الحجم: sm = صغير للبطاقات، md = متوسط، lg = للتفاصيل */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** يخفي الـ tooltip */
  hideTooltip?: boolean;
  /** يخفي الشارة لو غير موثّق */
  hideIfNone?: boolean;
  className?: string;
}

const TIER_CONFIG: Record<VerificationTier, { icon: string; label: string; tooltip: string; bg: string; border: string; text: string }> = {
  DIAMOND: {
    icon: '💎',
    label: 'موثّق ذهبي',
    tooltip: 'معلن موثّق ذهبياً — نفاذ + رخصة فال سارية + سجل أكثر من 6 شهور',
    bg: 'bg-gradient-to-br from-amber-50 to-yellow-100',
    border: 'border-amber-400',
    text: 'text-amber-800',
  },
  VERIFIED: {
    icon: '✓',
    label: 'معلن موثّق',
    tooltip: 'معلن موثّق — تحقّق نفاذ الوطني + رخصة فال سارية من الهيئة العامة للعقار',
    bg: 'bg-emerald-50',
    border: 'border-emerald-400',
    text: 'text-emerald-800',
  },
  IDENTITY: {
    icon: '🔒',
    label: 'محقّق هوية',
    tooltip: 'محقّق هويته عبر نفاذ الوطني',
    bg: 'bg-sky-50',
    border: 'border-sky-300',
    text: 'text-sky-800',
  },
  NONE: {
    icon: '⚠️',
    label: 'غير موثّق',
    tooltip: 'لم يكتمل تحقّق المعلن بعد',
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    text: 'text-gray-600',
  },
};

const SIZE_CONFIG = {
  xs: 'text-[9px] px-1.5 py-0.5 gap-0.5',
  sm: 'text-[10px] px-2 py-0.5 gap-1',
  md: 'text-xs px-2.5 py-1 gap-1',
  lg: 'text-sm px-3 py-1.5 gap-1.5',
};

export function VerifiedBadge({
  user,
  size = 'sm',
  hideTooltip = false,
  hideIfNone = true,
  className = '',
}: BadgeProps) {
  const tier = computeVerificationTier(user);
  if (hideIfNone && tier === 'NONE') return null;
  const cfg = TIER_CONFIG[tier];
  const sz = SIZE_CONFIG[size];

  return (
    <span
      className={`inline-flex items-center font-bold rounded-full border ${cfg.bg} ${cfg.border} ${cfg.text} ${sz} ${className}`}
      title={hideTooltip ? undefined : cfg.tooltip}
    >
      <span>{cfg.icon}</span>
      <span>{cfg.label}</span>
    </span>
  );
}

/** نسخة مفصّلة بدليل التحقّق (للسايدبار) */
export function VerificationDetails({ user }: { user: AdvertiserVerification | null | undefined }) {
  const tier = computeVerificationTier(user);
  const cfg = TIER_CONFIG[tier];

  const nafathOk = !!user?.nafathVerified;
  const falOk =
    user?.falLicenseStatus === 'APPROVED' &&
    (!user.falLicenseExpiresAt || new Date(user.falLicenseExpiresAt) > new Date());

  return (
    <div className={`rounded-2xl border-2 ${cfg.border} ${cfg.bg} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{cfg.icon}</span>
        <div>
          <div className={`font-black text-sm ${cfg.text}`}>{cfg.label}</div>
          <div className="text-[10px] text-gray-600">{cfg.tooltip}</div>
        </div>
      </div>

      <div className="space-y-1.5 text-xs">
        <Row ok={nafathOk} label="هوية نفاذ الوطني" />
        <Row ok={falOk} label="رخصة فال (الهيئة العامة للعقار)" />
        {tier === 'DIAMOND' && (
          <Row ok={true} label="سجل موثّق أكثر من 6 شهور" />
        )}
      </div>
    </div>
  );
}

function Row({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-700">{label}</span>
      <span className={`font-bold ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
        {ok ? '✓ مفعّل' : '✕ غير مفعّل'}
      </span>
    </div>
  );
}
